import { useState, type ComponentType } from 'react';
import { ArrowLeft, ArrowRight, Calculator, LineChart, ClipboardList, UserSquare, Scale } from 'lucide-react';
import { Card, CardContent, Button, Eyebrow, RaceBib } from '@/components/foundry';
import { cn } from '@/lib/utils';
import LifestyleTargetCalculator from '../components/labs/LifestyleTargetCalculator';
import FunnelAudienceCalculator from '../components/labs/FunnelAudienceCalculator';

/**
 * Labs — the portal that hosts AI Brand Lab's interactive tools. Two jobs:
 * teach (a low-friction, premium first touch) and demonstrate (proof of what an
 * AI-native company builds). "Rent the campus, own the brain, show the workshop."
 * Each tool also ships as an `abl-*` repo skill so members see both the hosted
 * tool AND how they'd build it themselves.
 */

type ToolId = 'lifestyle-target' | 'funnel-math';

interface ToolDef {
  id: string;
  status: 'live' | 'soon';
  module: string;
  title: string;
  desc: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  skill: string;
}

const TOOLS: ToolDef[] = [
  {
    id: 'lifestyle-target',
    status: 'live',
    module: 'M02 · L01',
    title: 'Lifestyle Target Calculator',
    desc: 'Work backwards from the freedom you want to the revenue your business needs.',
    icon: Calculator,
    skill: 'abl-lifestyle-target',
  },
  {
    id: 'funnel-math',
    status: 'live',
    module: 'M02 · L02',
    title: 'Funnel & Audience Calculator',
    desc: 'Work out how many leads, calls, or DMs it actually takes to hit your number.',
    icon: LineChart,
    skill: 'abl-funnel-math',
  },
  {
    id: 'current-state',
    status: 'soon',
    module: 'M02 · L03',
    title: 'Current-State Intake',
    desc: 'Capture your business as it really is, so your AI co-founder reasons from reality.',
    icon: ClipboardList,
    skill: 'abl-current-state',
  },
  {
    id: 'typed-standards',
    status: 'soon',
    module: 'M02 · L04',
    title: 'Typed Standards',
    desc: 'Turn your voice, rules, and limits into the standards your AI operates from.',
    icon: Scale,
    skill: 'abl-typed-standards',
  },
  {
    id: 'profile',
    status: 'soon',
    module: 'M02 · L05',
    title: 'Foundations Profile',
    desc: "Put it all in one place, and stay clear about which numbers are real and which are still a guess.",
    icon: UserSquare,
    skill: 'abl-profile',
  },
];

function ToolCard({ tool, onOpen }: { tool: ToolDef; onOpen: (id: ToolId) => void }) {
  const Icon = tool.icon;
  const live = tool.status === 'live';
  return (
    <Card
      className={cn(
        'flex flex-col bg-fd-paper transition-colors',
        live ? 'hover:border-fd-text' : 'opacity-75',
      )}
    >
      <CardContent className="flex flex-1 flex-col pt-[22px]">
        <div className="flex items-start justify-between">
          <span className="grid size-10 place-items-center rounded-fd border border-fd-border bg-fd-surface-1 text-fd-text">
            <Icon className="size-[18px]" strokeWidth={1.75} />
          </span>
          {live ? (
            <span className="inline-flex items-center gap-1.5 rounded-[5px] border border-fd-teal/30 bg-fd-teal/10 px-2 py-[3px] font-geist-mono text-[9.5px] font-medium uppercase tracking-[0.05em] text-fd-teal">
              <span className="size-1.5 rounded-full bg-fd-teal" /> Live
            </span>
          ) : (
            <span className="rounded-[5px] border border-fd-border px-2 py-[3px] font-geist-mono text-[9.5px] font-medium uppercase tracking-[0.05em] text-fd-text-subtle">
              Soon
            </span>
          )}
        </div>

        <Eyebrow className="mt-4">{tool.module}</Eyebrow>
        <h3 className="mt-1.5 font-geist text-[16px] font-medium tracking-[-0.01em] text-fd-text">{tool.title}</h3>
        <p className="mt-1.5 flex-1 text-[13px] leading-[1.5] text-fd-text-muted">{tool.desc}</p>

        <div className="mt-4 flex items-center justify-between border-t border-fd-border/70 pt-3">
          <span className="font-geist-mono text-[10.5px] text-fd-text-subtle">
            skill · <span className="text-fd-text-muted">{tool.skill}</span>
          </span>
          {live ? (
            <Button variant="primary" size="sm" onClick={() => onOpen(tool.id as ToolId)}>
              Open <ArrowRight />
            </Button>
          ) : (
            <Button variant="ghost" size="sm" disabled>
              Coming soon
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Labs({ saveMode = 'profile' }: { saveMode?: 'profile' | 'email' }) {
  const [tool, setTool] = useState<ToolId | null>(null);

  if (tool) {
    return (
      <div className="mx-auto max-w-[1080px] px-8 py-8">
        <button
          type="button"
          onClick={() => setTool(null)}
          className="mb-6 inline-flex items-center gap-1.5 font-geist-mono text-[11px] font-medium uppercase tracking-[0.06em] text-fd-text-muted transition-colors hover:text-fd-text"
        >
          <ArrowLeft className="size-3.5" strokeWidth={2} /> All tools
        </button>
        {tool === 'lifestyle-target' && <LifestyleTargetCalculator saveMode={saveMode} />}
        {tool === 'funnel-math' && <FunnelAudienceCalculator saveMode={saveMode} />}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] px-8 py-10">
      <header className="max-w-[64ch]">
        <div className="mb-4 flex items-center gap-3">
          <RaceBib n="A" tone="race" size="lg" shadow="sm" className="-rotate-3" />
          <Eyebrow>Labs · Interactive tools</Eyebrow>
        </div>
        <h1 className="font-geist text-[33px] font-medium leading-[1.04] tracking-[-0.022em] text-fd-text">
          Tools we built, so you can feel what AI-native building looks like.
        </h1>
        <p className="mt-3 text-[15px] leading-[1.6] text-fd-text-muted">
          Each one is a real interactive tool from the program, and each ships as a skill you can run
          in your own repo. Use them here, then build your own. That is the whole point.
        </p>
      </header>

      <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {TOOLS.map((t) => (
          <ToolCard key={t.id} tool={t} onOpen={setTool} />
        ))}
      </div>
    </div>
  );
}
